import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { LogIn, ArrowRight, Newspaper } from "lucide-react";
import { toast } from "sonner";
import PageHero from "../components/ui-custom/PageHero";
import GlassCard from "../components/ui-custom/GlassCard";
import Reveal from "../components/ui-custom/Reveal";
import TradingViewWidget from "../components/investors/TradingViewWidget";
import StockChart from "../components/investors/StockChart";
import { investorSections } from "../data/mock";
import { getIcon } from "../data/iconMap";
import { useAuth } from "../context/AuthContext";

const anchorIds = { "Corporate Governance": "governance", "Investor Relations": "relations", "Stock Exchange Disclosures": "disclosures", "Quarterly Earnings": "earnings" };

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const TV_SYMBOL = "NSE:QPOWER";

const formatNewsDate = (pubDate) => {
  if (!pubDate) return "";
  const d = new Date(pubDate);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const NewsFeed = () => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/investors/news`)
      .then((res) => setItems(res.data.items || []))
      .catch(() => setItems([]));
  }, []);

  return (
    <GlassCard className="p-7">
      <div className="flex items-center gap-3 mb-5">
        <span className="w-11 h-11 rounded-xl flex items-center justify-center bg-qcyan/10 border border-qcyan/25">
          <Newspaper size={20} className="text-qcyan" />
        </span>
        <div>
          <h3 className="font-display font-semibold text-foreground text-lg">Latest News</h3>
          <p className="text-xs text-muted-foreground">Auto-updated coverage of Quality Power &amp; leadership</p>
        </div>
      </div>

      {items === null && (
        <p className="text-sm text-muted-foreground">Loading latest headlines…</p>
      )}
      {items !== null && items.length === 0 && (
        <p className="text-sm text-muted-foreground">No recent headlines found.</p>
      )}
      <ul className="space-y-4">
        {items?.slice(0, 8).map((item) => (
          <li key={item.link} className="border-b border-border/50 pb-4 last:border-none last:pb-0">
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-foreground hover:text-qcyan transition-colors leading-snug"
            >
              {item.title}
            </a>
            <p className="text-xs text-muted-foreground mt-1">
              {item.source || "News"}
              {item.pub_date ? ` · ${formatNewsDate(item.pub_date)}` : ""}
            </p>
          </li>
        ))}
      </ul>
    </GlassCard>
  );
};

const LiveFinancials = () => {
  const { resolvedTheme, theme } = useTheme();
  const colorTheme = (resolvedTheme || theme) === "dark" ? "dark" : "light";

  return (
    <GlassCard className="p-7">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h3 className="font-display font-semibold text-foreground text-lg">Live Stock &amp; Financials</h3>
          <p className="text-xs text-muted-foreground">NSE: QPOWER · BSE: 544367 · Live market data via TradingView</p>
        </div>
        <a
          href="https://www.screener.in/company/QPOWER/consolidated/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-semibold text-qcyan hover:underline inline-flex items-center gap-1"
        >
          Detailed ratios on Screener.in <ArrowRight size={12} />
        </a>
      </div>

      <TradingViewWidget
        scriptSrc="https://s3.tradingview.com/external-embedding/embed-widget-symbol-info.js"
        config={{ symbol: TV_SYMBOL, width: "100%", colorTheme, isTransparent: true, locale: "en" }}
        height={180}
      />
      <div className="mt-5">
        <TradingViewWidget
          scriptSrc="https://s3.tradingview.com/external-embedding/embed-widget-financials.js"
          config={{
            symbol: TV_SYMBOL,
            colorTheme,
            isTransparent: true,
            displayMode: "regular",
            width: "100%",
            height: 500,
            locale: "en",
          }}
          height={500}
        />
      </div>
    </GlassCard>
  );
};

const Investors = () => {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();

  const handleSignIn = async () => {
    try {
      const signedInUser = await login();
      if (signedInUser) {
        navigate(signedInUser.is_employee ? "/employees" : "/portal");
      }
    } catch (err) {
      toast.error("Sign-in failed. Please try again.");
    }
  };

  return (
    <div>
      <PageHero
        eyebrow="Investors"
        title="Investor Relations"
        subtitle="Quality Power Electrical Equipments Limited is listed on the BSE & NSE. Explore governance, disclosures and financial communications."
      />

      <section className="px-6 pb-10">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <GlassCard accent="amber" className="p-7 flex flex-wrap items-center justify-between gap-5">
              <div>
                <p className="section-eyebrow mb-2">Client &amp; Partner Portal</p>
                <h3 className="font-display font-semibold text-foreground text-lg mb-1">
                  Track orders, invoices, projects and rewards
                </h3>
                <p className="text-sm text-muted-foreground max-w-xl">
                  Sign in with Google for gated access to your Quality Power partner dashboard.
                </p>
              </div>
              {isAuthenticated ? (
                <Link to="/portal" className="btn-glow-primary px-6 py-3 rounded-md text-sm font-semibold inline-flex items-center gap-2">
                  Go to Portal <ArrowRight size={16} />
                </Link>
              ) : (
                <button onClick={handleSignIn} className="btn-glow-primary px-6 py-3 rounded-md text-sm font-semibold inline-flex items-center gap-2">
                  <LogIn size={16} /> Sign In to Access
                </button>
              )}
            </GlassCard>
          </Reveal>
        </div>
      </section>

      <section className="px-6 pb-10">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <GlassCard className="p-5 sm:p-7">
              <StockChart />
            </GlassCard>
          </Reveal>
        </div>
      </section>

      <section className="px-6 pb-16">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-5 gap-6 items-start">
          <Reveal className="lg:col-span-3">
            <LiveFinancials />
          </Reveal>
          <Reveal delay={0.08} className="lg:col-span-2">
            <NewsFeed />
          </Reveal>
        </div>
      </section>

      <section className="px-6 pb-28">
        <div className="max-w-5xl mx-auto grid sm:grid-cols-2 gap-6">
          {investorSections.map((sec, i) => {
            const Icon = getIcon(sec.icon);
            return (
              <Reveal key={sec.title} delay={i * 0.08}>
                <GlassCard id={anchorIds[sec.title]} tilt className="p-7 h-full scroll-mt-28">
                  <span className="w-14 h-14 rounded-xl flex items-center justify-center bg-qcyan/10 border border-qcyan/25 mb-5">
                    <Icon size={26} className="text-qcyan" />
                  </span>
                  <h3 className="font-display font-semibold text-foreground text-lg mb-2">{sec.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{sec.detail}</p>
                </GlassCard>
              </Reveal>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default Investors;
